import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Typography,
  message,
} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import {
  getServiceProviderWithdrawRule,
  updateServiceProviderWithdrawRule,
} from '../../services/staffWithdrawRuleService';

interface StaffWithdrawRuleFormValues {
  securityDepositAmount: number;
  settlementDelayDays: number;
  settlementFlowMode: 'order_completed' | 'sub_order_completed';
  withdrawAuditMode: 'immediate_arrival' | 'manual_review';
  withdrawExecuteMode: 'mock' | 'real';
  withdrawServiceFeeBearer: 'platform' | 'provider';
  wechatMchId: string;
  wechatAppId: string;
  wechatSerialNo: string;
  wechatPayPublicKeyId: string;
  wechatApiV3Key: string;
  wechatWithdrawNotifyUrl: string;
  wechatDepositPayNotifyUrl: string;
  wechatPayPublicKey: string;
  wechatPrivateKey: string;
  wechatConfigRemark: string;
}

const StaffWithdrawRulePage: React.FC = () => {
  const [form] = Form.useForm<StaffWithdrawRuleFormValues>();
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const withdrawExecuteMode = Form.useWatch('withdrawExecuteMode', form);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getServiceProviderWithdrawRule();
      const data = res.data;

      form.setFieldsValue({
        securityDepositAmount: data?.securityDepositAmount ?? 0,
        settlementDelayDays: data?.settlementDelayDays ?? 0,
        settlementFlowMode: data?.settlementFlowMode ?? 'sub_order_completed',
        withdrawAuditMode: data?.withdrawAuditMode ?? 'manual_review',
        withdrawExecuteMode: data?.withdrawExecuteMode ?? 'mock',
        withdrawServiceFeeBearer: data?.withdrawServiceFeeBearer ?? 'platform',
        wechatMchId: data?.wechatMchId ?? '',
        wechatAppId: data?.wechatAppId ?? '',
        wechatSerialNo: data?.wechatSerialNo ?? '',
        wechatPayPublicKeyId: data?.wechatPayPublicKeyId ?? '',
        wechatApiV3Key: data?.wechatApiV3Key ?? '',
        wechatWithdrawNotifyUrl: data?.wechatWithdrawNotifyUrl ?? '',
        wechatDepositPayNotifyUrl: data?.wechatDepositPayNotifyUrl ?? '',
        wechatPayPublicKey: data?.wechatPayPublicKey ?? '',
        wechatPrivateKey: data?.wechatPrivateKey ?? '',
        wechatConfigRemark: data?.wechatConfigRemark ?? '',
      });
    } catch {
      message.error('加载服务人员提现规则失败');
      form.setFieldsValue({
        securityDepositAmount: 0,
        settlementDelayDays: 0,
        settlementFlowMode: 'sub_order_completed',
        withdrawAuditMode: 'manual_review',
        withdrawExecuteMode: 'mock',
        withdrawServiceFeeBearer: 'platform',
        wechatMchId: '',
        wechatAppId: '',
        wechatSerialNo: '',
        wechatPayPublicKeyId: '',
        wechatApiV3Key: '',
        wechatWithdrawNotifyUrl: '',
        wechatDepositPayNotifyUrl: '',
        wechatPayPublicKey: '',
        wechatPrivateKey: '',
        wechatConfigRemark: '',
      });
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitLoading(true);
      await updateServiceProviderWithdrawRule({
        allowAlipay: false,
        allowWechatWallet: true,
        allowBankCard: false,
        securityDepositAmount: values.securityDepositAmount ?? 0,
        settlementDelayDays: values.settlementDelayDays,
        settlementFlowMode: values.settlementFlowMode,
        withdrawAuditMode: values.withdrawAuditMode,
        withdrawExecuteMode: values.withdrawExecuteMode,
        withdrawServiceFeeBearer: values.withdrawServiceFeeBearer,
        wechatMchId: values.wechatMchId || '',
        wechatAppId: values.wechatAppId || '',
        wechatSerialNo: values.wechatSerialNo || '',
        wechatPayPublicKeyId: values.wechatPayPublicKeyId || '',
        wechatApiV3Key: values.wechatApiV3Key || '',
        wechatWithdrawNotifyUrl: values.wechatWithdrawNotifyUrl || '',
        wechatDepositPayNotifyUrl: values.wechatDepositPayNotifyUrl || '',
        wechatPayPublicKey: values.wechatPayPublicKey || '',
        wechatPrivateKey: values.wechatPrivateKey || '',
        wechatConfigRemark: values.wechatConfigRemark || '',
      });
      message.success('保存成功');
      await loadData();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.message || '保存服务人员提现规则失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Card
        loading={loading}
        title={(
          <Space size={8}>
            <DollarOutlined />
            <span>服务人员提现规则</span>
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => void loadData()}>
              刷新
            </Button>
            <Button type="primary" icon={<SaveOutlined />} loading={submitLoading} onClick={() => void handleSubmit()}>
              保存配置
            </Button>
          </Space>
        )}
      >
        <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
          当前版本仅支持微信提现。你可以在这里配置提现审核方式、模拟/真实提现，以及真实微信提现所需的商户参数。
        </Typography.Paragraph>

        <Form<StaffWithdrawRuleFormValues>
          form={form}
          layout="vertical"
          initialValues={{
            settlementDelayDays: 0,
            securityDepositAmount: 0,
            settlementFlowMode: 'sub_order_completed',
            withdrawAuditMode: 'manual_review',
            withdrawExecuteMode: 'mock',
            withdrawServiceFeeBearer: 'platform',
            wechatMchId: '',
            wechatAppId: '',
            wechatSerialNo: '',
            wechatPayPublicKeyId: '',
            wechatApiV3Key: '',
            wechatWithdrawNotifyUrl: '',
            wechatDepositPayNotifyUrl: '',
            wechatPayPublicKey: '',
            wechatPrivateKey: '',
            wechatConfigRemark: '',
          }}
        >
          <Form.Item label="提现方式" extra="当前仅支持微信提现，支付宝和银行卡已移除。">
            <Radio.Group value="wechat" disabled>
              <Radio value="wechat">微信提现</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="结算流水配置"
            name="settlementFlowMode"
            rules={[{ required: true, message: '请选择结算流水配置' }]}
            extra="主单完成：主单全部完成后统一补生成未生成的子单流水；子单完成：每个子单完成时立即生成对应流水。"
          >
            <Radio.Group>
              <Radio value="order_completed">主单完成生成流水</Radio>
              <Radio value="sub_order_completed">子单完成生成流水</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="订单完成后结算延迟天数"
            name="settlementDelayDays"
            rules={[
              { required: true, message: '请输入结算延迟天数' },
              { type: 'number', min: 0, max: 3650, message: '结算延迟天数需在 0 到 3650 之间' },
            ]}
            extra="订单完成后满 N 天，才允许进入结算/提现。0 表示完成后即可结算。"
          >
            <InputNumber min={0} max={3650} precision={0} style={{ width: 220 }} placeholder="请输入天数" />
          </Form.Item>

          <Form.Item
            label="保证金"
            name="securityDepositAmount"
            rules={[
              { required: true, message: '请输入保证金' },
              { type: 'number', min: 0, message: '保证金不能小于 0' },
            ]}
            extra="服务人员提现规则中的保证金配置，0 表示不设置保证金。"
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: 220 }}
              addonBefore="￥"
              placeholder="请输入保证金"
            />
          </Form.Item>

          <Form.Item
            label="提醒审核"
            name="withdrawAuditMode"
            rules={[{ required: true, message: '请选择提醒审核方式' }]}
            extra="立即到账：前端按立即到账文案提示；人工审核：前端按提交后进入人工审核文案提示。"
          >
            <Radio.Group>
              <Radio value="immediate_arrival">立即到账</Radio>
              <Radio value="manual_review">人工审核</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="美团商家营销费承担"
            name="withdrawServiceFeeBearer"
            rules={[{ required: true, message: '请选择美团商家营销费承担方' }]}
            extra="平台承担：美团商家营销费由平台承担，预估金额不扣减；服务人员承担：美团订单预估金额先扣除商家营销费后再计算。"
          >
            <Radio.Group>
              <Radio value="platform">平台承担</Radio>
              <Radio value="provider">服务人员承担</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label="提现执行模式"
            name="withdrawExecuteMode"
            rules={[{ required: true, message: '请选择提现执行模式' }]}
            extra="模拟提现：仅记录提现申请和状态，不实际调用微信接口。真实提现：按微信提现配置进行真实出款。"
          >
            <Radio.Group>
              <Radio value="mock">模拟提现</Radio>
              <Radio value="real">真实提现</Radio>
            </Radio.Group>
          </Form.Item>

          {withdrawExecuteMode === 'real' && (
            <>
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 24 }}
                message="真实提现将依赖微信商户配置"
                description="请确保商户号、AppId、商户证书序列号、APIv3Key 和商户私钥均已填写正确，否则真实微信提现无法发起。"
              />

              <Divider>微信提现配置</Divider>

              <Form.Item
                label="微信商户号"
                name="wechatMchId"
                rules={[{ required: true, message: '请输入微信商户号' }]}
              >
                <Input placeholder="请输入微信商户号 mchId" />
              </Form.Item>

              <Form.Item
                label="微信应用 AppId"
                name="wechatAppId"
                rules={[{ required: true, message: '请输入微信应用 AppId' }]}
                extra="通常填写微信小程序 AppId。"
              >
                <Input placeholder="请输入微信应用 AppId" />
              </Form.Item>

              <Form.Item
                label="商户证书序列号"
                name="wechatSerialNo"
                rules={[{ required: true, message: '请输入商户证书序列号' }]}
              >
                <Input placeholder="请输入商户证书序列号" />
              </Form.Item>

              <Form.Item
                label="微信支付公钥 ID"
                name="wechatPayPublicKeyId"
                rules={[{ required: true, message: '请输入微信支付公钥 ID' }]}
              >
                <Input placeholder="请输入微信支付公钥 ID" />
              </Form.Item>

              <Form.Item
                label="APIv3Key"
                name="wechatApiV3Key"
                rules={[{ required: true, message: '请输入 APIv3Key' }]}
              >
                <Input.Password placeholder="请输入 APIv3Key" />
              </Form.Item>

              <Form.Item
                label="提现回调地址"
                name="wechatWithdrawNotifyUrl"
                extra="用于接收微信提现/转账结果通知，例如：https://your-domain.com/api/payment/wechat/withdraw-notify"
              >
                <Input placeholder="请输入微信提现回调地址" />
              </Form.Item>

              <Form.Item
                label="保证金支付回调地址"
                name="wechatDepositPayNotifyUrl"
                extra="用于服务人员支付保证金后的微信支付回调，例如：https://your-domain.com/api/payment/wechat/deposit-notify"
              >
                <Input placeholder="请输入保证金支付回调地址" />
              </Form.Item>

              <Form.Item
                label="微信支付公钥"
                name="wechatPayPublicKey"
                rules={[{ required: true, message: '请输入微信支付公钥' }]}
              >
                <Input.TextArea rows={8} placeholder="请输入微信支付公钥 PEM 内容" />
              </Form.Item>

              <Form.Item
                label="商户私钥"
                name="wechatPrivateKey"
                rules={[{ required: true, message: '请输入商户私钥' }]}
                extra="支持粘贴 PEM 内容。"
              >
                <Input.TextArea rows={8} placeholder="请输入商户私钥 PEM 内容" />
              </Form.Item>

              <Form.Item
                label="配置说明"
                name="wechatConfigRemark"
                extra="可选，用于记录当前微信提现配置说明、环境或证书备注。"
              >
                <Input placeholder="例如：生产环境商户配置 / 2026-04 更新" />
              </Form.Item>
            </>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default StaffWithdrawRulePage;
